import DOMPurify from 'dompurify';

/**
 * Sanitization utilities to prevent XSS attacks
 * 
 * These functions use DOMPurify to safely sanitize HTML content
 * before rendering with v-html or other DOM manipulation.
 */

/**
 * Sanitizes HTML content, allowing only safe tags and attributes
 * This is a strict configuration suitable for user-generated content
 */
export function sanitizeHtml(dirty: string): string {
  if (!dirty) {
    return '';
  }

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'span', 'mark', 'br', 'p', 'div', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['class', 'style'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  });
}

/**
 * Sanitizes HTML for search highlighting
 * Only allows span and mark tags with specific classes for highlighting
 */
export function sanitizeHighlight(dirty: string): string {
  if (!dirty) {
    return '';
  }

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['span', 'mark'],
    ALLOWED_ATTR: ['class'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  });
}

/**
 * Sanitizes rich text content (for admin-controlled content like disclaimers)
 * Allows a broader set of tags but still prevents script execution
 */
export function sanitizeRichText(dirty: string): string {
  if (!dirty) {
    return '';
  }

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'b', 'i', 'em', 'strong', 'u', 'span', 'p', 'div', 'br',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'blockquote',
    ],
    ALLOWED_ATTR: ['class', 'style', 'href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
  });
}

/**
 * Escapes HTML to plain text (no HTML tags allowed)
 * Use this when you need to display user input as-is without any HTML
 */
export function escapeHtml(text: string): string {
  if (!text) {
    return '';
  }

  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Safe highlight function that escapes input before adding highlight markup
 * This prevents XSS while still allowing search highlighting
 */
export function safeHighlight(text: string, query: string): string {
  if (!query || query.trim().length === 0) {
    return escapeHtml(text);
  }

  if (!text || text.trim().length === 0) {
    return '';
  }

  // Escape both the text and query to prevent XSS
  const escapedText = escapeHtml(text);
  
  // Escape special regex characters in the query
  const specialChars = /[^a-z\d\s]/gi;
  const escapedQuery = query.replace(specialChars, (matched) => '\\' + matched);

  const expr = new RegExp(escapedQuery, 'ig');
  const highlighted = escapedText.replace(
    expr,
    (matched) => `<span class="text-base-100 bg-secondary/75 rounded-sm">${matched}</span>`,
  );

  // Sanitize the result to ensure only our highlight spans are present
  return sanitizeHighlight(highlighted);
}

